import {clsx} from 'clsx'
import {useEffect, useRef} from 'react'

import {useInlineCommentDialogModeContext} from '../contexts/InlineCommentDialogModeContext'
import type {DiffAnnotation} from '../types'
import {hideInteractiveElements, showInteractiveElements} from '../util/hide-show-interactive-elements'
import {Annotation} from './Annotation'
import {AnnotationPresentationMap} from './AnnotationIcon'
import styles from './InlineAnnotation.module.css'

interface InlineAnnotationProps {
  annotation: DiffAnnotation
  isFirstMarker?: boolean
}

/**
 * Inline wrapper for the Annotation component
 * Used within inline markers to display annotations in the context of code
 */
export function InlineAnnotation({annotation, isFirstMarker}: InlineAnnotationProps) {
  const annotationRef = useRef<HTMLDivElement>(null)
  const {isInDialogMode} = useInlineCommentDialogModeContext()

  useEffect(() => {
    if (!isInDialogMode) {
      hideInteractiveElements(annotationRef.current)
    } else {
      showInteractiveElements(annotationRef.current)
    }
  }, [isInDialogMode])

  return (
    <div
      id={`annotation_${annotation.databaseId}`}
      ref={annotationRef}
      // When aria-label and role is set while still in gridcell mode, the browser doesn't the respect the
      // aria-hidden"true" and we end up with the aria-labels leaking into the accessible name of the gridcell,
      // which is not what we want. So we should only set these attributes while in dialog mode.
      {...(isInDialogMode
        ? {
            role: 'region',
            'aria-label': `${AnnotationPresentationMap[annotation.annotationLevel].ariaLabel}: ${annotation.title}`,
          }
        : {})}
      className={clsx(
        'border rounded-2 color-border-default color-shadow-small overflow-hidden',
        isFirstMarker ? 'mb-1' : 'mb-2',
        styles.inlineAnnotation,
      )}
      data-level={annotation.annotationLevel}
      data-testid={`annotation-${annotation.id}`}
      data-marker-id={`${annotation.id}`}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
    >
      <Annotation annotation={annotation} />
    </div>
  )
}

try{ InlineAnnotation.displayName ||= 'InlineAnnotation' } catch {}